// components/sideBar/SideBar.tsx
"use client";

import { useTheme } from "next-themes";
import Image from "next/image";
import Link from "next/link";
import "./style.css";
import CommonCard from "../commonCard/CommonCard";
import BorderLine from "../borderLine/BorderLine";
import { useEffect, useState } from "react";
import type { SidebarData, FeaturedPost } from "@/types/sidebar-data";

interface SideBarNewsCardsProps {
  title: string;
  post: FeaturedPost[];
  loading: boolean;
}

// ── SideBarTopCard (unchanged) ────────────────────────────────────────────────
export function SideBarTopCard() {
  const [mounted, setMounted] = useState(false);
  const { resolvedTheme } = useTheme();

  useEffect(() => {
    setMounted(true);
  }, []);
  if (!mounted) return null;

  const isDark = resolvedTheme === "dark";

  return (
    <div className="side-bar-container">
      <div className="fw-card fw-brand-card border-0 fw-shadow-sm">
        <div className="fw-card-body rounded">
          <div className="mb-4">
            <Link
              href="/"
              className="fw-d-inline-flex align-items-center text-decoration-none"
            >
              <div className="d-flex align-items-center gap-2">
                {mounted && (
                  <Image
                    src={
                      isDark ? "/logo/logo-light.png" : "/logo/logo-dark.png"
                    }
                    height={40}
                    width={120}
                    alt="coinccino"
                    style={{ width: "180px", height: "36px" }}
                    priority
                  />
                )}
              </div>
            </Link>
          </div>
          <p className="brand-description text-secondary">
            The community for those passionate about blockchain and innovative
            technologies, uniting passion, knowledge and opportunity in the
            digital world
          </p>
        </div>
      </div>
    </div>
  );
}

// ── resolvePostLink (unchanged) ───────────────────────────────────────────────
export const resolvePostLink = (item: any) => {
  if (!item) return "/";
  const link = item.url || item.title_slug;
  if (!link) return "/";
  try {
    if (link.startsWith("http")) return new URL(link).pathname || "/";
    return `/${link.replace(/^\/+/, "")}`;
  } catch {
    return "/";
  }
};

// ── SideBarNewsCards (unchanged) ──────────────────────────────────────────────
export function SideBarNewsCards({
  title,
  post,
  loading,
}: SideBarNewsCardsProps) {
  return (
    <>
      <hr />
      <div className="py-md-4 px-md-2">
        <h3 className="gh-sidebar-featured-posts-title">{title}</h3>
        <div className="d-flex flex-column">
          {loading ? (
            <div style={{ color: "var(--muted-foreground)" }}>Loading...</div>
          ) : (
            Array.isArray(post) &&
            post.map((item, index) => (
              <div
                className="col-12 fw-card-container px-0"
                key={item.id}
                style={{ fontSize: "14px" }}
              >
                <CommonCard
                  authorName={item.author}
                  publishDate={item.date}
                  titleText={item.title}
                  imgSrc={item.image || "/image-not-found.png"}
                  authorSlug={item.authorSlug || ""}
                  cardLink={
                    item.url?.startsWith("http")
                      ? item.url
                      : `${process.env.NEXT_PUBLIC_BASE_URL}/${item.url}`
                  }
                  imgStyle={{
                    height: "88px",
                    width: "88px",
                    objectFit: "cover",
                  }}
                  compact
                  bodyClassName="py-0 px-2"
                  metaClassName="fw-meta-text"
                  authorClassName="side-bar-post-text"
                  authorSlugClassName="author-side-bar-post-text"
                  timeClassName="side-bar-post-text"
                  titleClassName="fs-6 gh-sidebar-feat-post-title"
                  dotSeparatoClassName="dot-separator"
                />
                {index !== post.length - 1 && <BorderLine />}
              </div>
            ))
          )}
        </div>
      </div>
    </>
  );
}

// ── SideBar — now accepts SSR data as props, NO client fetch ──────────────────
export default function SideBar({
  initialData,
}: {
  initialData?: SidebarData;
}) {
  const [popularPosts, setPopularPosts] = useState<FeaturedPost[]>(
    initialData?.popularPosts ?? [],
  );
  const [recommendedPosts, setRecommendedPosts] = useState<FeaturedPost[]>(
    initialData?.recommendedPosts ?? [],
  );
  // No loading state needed when SSR data is present
  const loading = false;

  return (
    <>
      <SideBarNewsCards
        title="Popular Posts"
        post={popularPosts}
        loading={false}
      />
      <SideBarNewsCards
        title="Recommended Posts"
        post={recommendedPosts}
        loading={false}
      />
    </>
  );
}
